export type LeadSource =
  | 'email'
  | 'linkedin'
  | 'instagram'
  | 'facebook'
  | 'twitter'
  | 'web_form'
  | 'scraping'
  | 'linkedin_enrichment'
  | 'linkedin_segment_search'
  | 'instagram_enrichment'
  | 'instagram_segment_search'
  | 'manual'
  | 'website'
  | 'referral'
  | 'import'
  | 'other'
  | 'event';

export interface FunnelStage {
  id: string;
  name: string;
  position: number;
  color: string;
  createdAt: string;
}

export interface FunnelTemplate {
  id: string;
  organizationId: string;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  stages: FunnelStage[];
  _count?: { leads: number };
}

export interface LeadUser {
  id: string;
  fullName: string;
  email: string;
}

export const CONTACT_ROLE_OPTIONS = [
  'decision_maker',
  'influencer',
  'technical',
  'financial',
  'end_user',
  'gatekeeper',
  'champion',
] as const;

export type ContactRole = (typeof CONTACT_ROLE_OPTIONS)[number];

export interface Contact {
  id: string;
  leadId: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  jobTitle: string | null;
  sourceUrl: string | null;
  headline: string | null;
  location: string | null;
  isPrimary: boolean;
  role: ContactRole | null;
  customFields?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface LeadProductReference {
  id: string;
  name: string;
  available: boolean;
  kind?: 'product' | 'service';
}

export interface LeadProductReferences {
  interest: LeadProductReference | null;
  suggestion: LeadProductReference | null;
}

export interface Lead {
  id: string;
  organizationId: string;
  funnelTemplateId: string;
  currentStageId: string;
  assignedTo?: string | null;
  email?: string | null;
  phone?: string | null;
  company?: string | null;
  fullName?: string | null;
  source: LeadSource;
  sourceUrl?: string | null;
  jobTitle?: string | null;
  qualificationStatus?: 'qualified' | 'not_qualified_title' | 'pending_validation';
  normalizedJobTitle?: string | null;
  titleScore?: number | null;
  customFields?: Record<string, unknown> | null;
  tags: string[];
  // Human-review queue, currently set only by an in-call consent refusal
  // (FLOSEAI-560).
  requiresReview?: boolean | null;
  requiresReviewReason?: string | null;
  requiresReviewAt?: string | null;
  // Was already returned by GET /leads/:id (no response DTO filters it out)
  // but never declared here — the calendar lead-invite guardrail (Commit 19)
  // needs it to know whether an attached lead is protected.
  doNotCall?: boolean;
  marketingOptIn?: boolean | null;
  lastContactedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  currentStage: FunnelStage;
  assignedUser?: LeadUser | null;
  contacts?: Contact[];
}

export interface LeadDetail extends Lead {
  productReferences?: LeadProductReferences;
  organization: { id: string; name: string };
  funnelTemplate: FunnelTemplate;
  notes: Note[];
  activities: Activity[];
}

export interface Activity {
  id: string;
  leadId: string;
  userId?: string | null;
  activityType: string;
  description?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  user?: { id: string; fullName: string } | null;
}

export type NoteAuthorType = 'user' | 'ai_agent' | 'system';

export interface Note {
  id: string;
  leadId: string;
  userId: string;
  content: string;
  authorType: NoteAuthorType;
  agentType?: string | null;
  createdAt: string;
  updatedAt: string;
  user: { id: string; fullName: string };
}

export interface BoardStage extends FunnelStage {
  leads: Lead[];
  count: number;
}

export interface BoardVisibilityInfo {
  mode: 'individual' | 'shared' | 'hybrid';
  viewMode: 'my' | 'team';
  canViewTeam: boolean;
  isReadOnly: boolean;
}

export interface BoardData {
  template: {
    id: string;
    name: string;
    description?: string;
    isActive: boolean;
  };
  stages: BoardStage[];
  visibility: BoardVisibilityInfo;
}

export interface LeadsResponse {
  leads: Lead[];
  total: number;
  limit: number;
  offset: number;
}

export interface ActivitiesResponse {
  items: Activity[];
  total: number;
  nextCursor: string | null;
}

export interface CreateLeadPayload {
  organizationId: string;
  funnelTemplateId: string;
  email?: string;
  phone?: string;
  company?: string;
  fullName?: string;
  source: LeadSource;
  sourceUrl?: string;
  jobTitle?: string;
  customFields?: Record<string, unknown>;
  tags?: string[];
  assignedTo?: string;
  primaryContactRole?: ContactRole;
}

export interface UpdateLeadPayload {
  email?: string | null;
  phone?: string | null;
  company?: string | null;
  fullName?: string;
  jobTitle?: string | null;
  sourceUrl?: string;
  customFields?: Record<string, unknown>;
  tags?: string[];
  assignedTo?: string | null;
}

export interface JobTitleFilter {
  id: string;
  organizationId: string;
  title: string;
  isActive: boolean;
  country: string;
  industry: string;
  scoreBonus: number;
  createdAt: string;
  updatedAt: string;
}

export interface JobTitleSynonym {
  id: string;
  organizationId: string;
  synonym: string;
  canonicalTitle: string;
  createdAt: string;
}

export interface AnalysisResult {
  score?: number;
  sentiment?: string;
  intent?: string;
  companySize?: string;
  industry?: string;
  keyInsights?: string[];
}

export type SellerType =
  | 'sdr'
  | 'closer'
  | 'hunter'
  | 'farmer'
  | 'account_executive'
  | 'full_cycle';

export type SellerStatus = 'active' | 'inactive' | 'on_leave';

export const SELLER_TYPE_LABELS: Record<SellerType, string> = {
  sdr: 'SDR',
  closer: 'Closer',
  hunter: 'Hunter',
  farmer: 'Farmer',
  account_executive: 'Account Executive',
  full_cycle: 'Full Cycle',
};

export const SELLER_STATUS_LABELS: Record<SellerStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  on_leave: 'On Leave',
};

export interface Industry {
  id: string;
  organizationId: string;
  name: string;
  isDefault: boolean;
  createdAt: string;
}

export interface SellerProfile {
  id: string;
  organizationId: string;
  userId: string;
  sellerType: SellerType;
  status: SellerStatus;
  maxLeads: number | null;
  bio: string | null;
  phone: string | null;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    fullName: string;
    email: string;
  };
  industries: Industry[];
  currentLeadCount?: number;
}
